// Phase 2: Auth State Transition Coordinator
// Prevents race conditions between sessionTimeout and security guard

interface AuthTransition {
  type: 'navigation' | 'signout' | 'state_change';
  startTime: number;
  userId?: string;
  description?: string;
}

class AuthStateCoordinator {
  private activeTransitions: Map<string, AuthTransition> = new Map();
  private readonly TRANSITION_TIMEOUT = 5000; // 5 seconds max for any transition
  
  startTransition(type: AuthTransition['type'], userId?: string, description?: string): string {
    const transitionId = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const transition: AuthTransition = {
      type,
      startTime: Date.now(),
      userId,
      description
    };
    
    this.activeTransitions.set(transitionId, transition);
    
    console.log(`[AuthCoordinator] Started transition:`, { transitionId, transition });
    
    // Auto-cleanup after timeout
    setTimeout(() => {
      if (this.activeTransitions.has(transitionId)) {
        console.log(`[AuthCoordinator] Auto-cleanup transition:`, transitionId);
        this.activeTransitions.delete(transitionId);
      }
    }, this.TRANSITION_TIMEOUT);
    
    return transitionId;
  }
  
  endTransition(transitionId: string): void {
    const transition = this.activeTransitions.get(transitionId);
    if (transition) {
      const duration = Date.now() - transition.startTime;
      console.log(`[AuthCoordinator] Ended transition:`, { transitionId, duration });
      this.activeTransitions.delete(transitionId);
    }
  }
  
  isTransitionActive(type?: AuthTransition['type']): boolean {
    if (!type) {
      return this.activeTransitions.size > 0;
    }
    
    for (const transition of this.activeTransitions.values()) {
      if (transition.type === type) {
        return true;
      }
    }
    return false;
  }
  
  getAllActiveTransitions(): AuthTransition[] {
    return Array.from(this.activeTransitions.values());
  }
  
  shouldBlockSignout(): boolean {
    // Block signout during navigation transitions
    return this.isTransitionActive('navigation') || this.isTransitionActive('state_change');
  }
  
  shouldBlockNavigation(): boolean {
    // Block navigation during signout transitions
    return this.isTransitionActive('signout');
  }
  
  logTransitionState(): void {
    console.log(`[AuthCoordinator] Active transitions:`, this.getAllActiveTransitions());
  }
}

// Singleton instance
export const authStateCoordinator = new AuthStateCoordinator();

// Helper functions for common patterns
export const withAuthTransition = async <T>(
  type: AuthTransition['type'],
  operation: () => Promise<T>,
  userId?: string,
  description?: string
): Promise<T> => {
  const transitionId = authStateCoordinator.startTransition(type, userId, description);
  try {
    const result = await operation();
    authStateCoordinator.endTransition(transitionId);
    return result;
  } catch (error) {
    authStateCoordinator.endTransition(transitionId);
    throw error;
  }
};